// ═══════════════════════════════════════════════════════════════
// SCHOLARSHIP SERVICE
// Path: src/services/scholarshipService.js
//
// All Supabase operations for named scholarship tracking:
//   scholarships, scholarship_donors, scholarship_awards
//
// Season = academic year (Sep–Aug), e.g., "2025-2026"
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

// ── Season helpers ────────────────────────────────────────────

export const getCurrentSeason = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed
  return month >= 8 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
};

export const getSeasonDates = (season) => {
  const [startYear] = season.split("-").map(Number);
  return {
    start: `${startYear}-09-01`,
    end: `${startYear + 1}-08-31`,
  };
};

export const getSeasonOptions = () => {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let yr = currentYear + 1; yr >= currentYear - 6; yr--) {
    options.push(`${yr}-${yr + 1}`);
  }
  return options;
};

// ═══════════════════════════════════════════════════════════════
// SECTION 1: SCHOLARSHIPS
// ═══════════════════════════════════════════════════════════════

export const scholarshipsService = {
  async getAll() {
    try {
      const { data, error } = await supabase
        .from("scholarships")
        .select("*")
        .order("is_active", { ascending: false })
        .order("name", { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      debugLog.error("scholarshipsService.getAll", error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase
        .from("scholarships")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      debugLog.error("scholarshipsService.getById", error);
      throw error;
    }
  },

  async create(scholarshipData) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("scholarships")
        .insert({
          name: scholarshipData.name,
          description: scholarshipData.description || null,
          is_active: scholarshipData.isActive !== false,
          created_by: user?.id || null,
        })
        .select()
        .single();

      if (error) throw error;
      debugLog.action("Scholarship created", { name: scholarshipData.name });
      return data;
    } catch (error) {
      debugLog.error("scholarshipsService.create", error);
      throw error;
    }
  },

  async update(id, updates) {
    try {
      const payload = { updated_at: new Date().toISOString() };
      if (updates.name !== undefined) payload.name = updates.name;
      if (updates.description !== undefined) payload.description = updates.description;
      if (updates.isActive !== undefined) payload.is_active = updates.isActive;

      const { data, error } = await supabase
        .from("scholarships")
        .update(payload)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      debugLog.action("Scholarship updated", { id });
      return data;
    } catch (error) {
      debugLog.error("scholarshipsService.update", error);
      throw error;
    }
  },

  async deactivate(id) {
    return this.update(id, { isActive: false });
  },

  async remove(id) {
    try {
      const { error } = await supabase
        .from("scholarships")
        .delete()
        .eq("id", id);

      if (error) throw error;
      debugLog.action("Scholarship deleted", { id });
    } catch (error) {
      debugLog.error("scholarshipsService.remove", error);
      throw error;
    }
  },

  async getForDropdown() {
    try {
      const { data, error } = await supabase
        .from("scholarships")
        .select("id, name")
        .eq("is_active", true)
        .order("name", { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      debugLog.error("scholarshipsService.getForDropdown", error);
      return [];
    }
  },
};

// ═══════════════════════════════════════════════════════════════
// SECTION 2: SCHOLARSHIP DONORS
// ═══════════════════════════════════════════════════════════════

export const scholarshipDonorsService = {
  async getByScholarship(scholarshipId) {
    try {
      const { data, error } = await supabase
        .from("scholarship_donors")
        .select(
          "id, scholarship_id, member_id, since_year, notes, created_at, members (id, first_name, last_name, credentialed_full_name, email)"
        )
        .eq("scholarship_id", scholarshipId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      debugLog.error("scholarshipDonorsService.getByScholarship", error);
      throw error;
    }
  },

  async link(scholarshipId, memberId, sinceYear = null, notes = null) {
    try {
      const { data, error } = await supabase
        .from("scholarship_donors")
        .upsert(
          {
            scholarship_id: scholarshipId,
            member_id: memberId,
            since_year: sinceYear,
            notes,
          },
          { onConflict: "scholarship_id,member_id", ignoreDuplicates: true }
        )
        .select()
        .single();

      if (error) throw error;
      debugLog.action("Scholarship donor linked", { scholarshipId, memberId });
      return data;
    } catch (error) {
      debugLog.error("scholarshipDonorsService.link", error);
      throw error;
    }
  },

  async unlink(id) {
    try {
      const { error } = await supabase
        .from("scholarship_donors")
        .delete()
        .eq("id", id);

      if (error) throw error;
      debugLog.action("Scholarship donor unlinked", { id });
    } catch (error) {
      debugLog.error("scholarshipDonorsService.unlink", error);
      throw error;
    }
  },
};

// ═══════════════════════════════════════════════════════════════
// SECTION 3: SCHOLARSHIP AWARDS
// ═══════════════════════════════════════════════════════════════

export const scholarshipAwardsService = {
  async getByScholarship(scholarshipId) {
    try {
      const { data, error } = await supabase
        .from("scholarship_awards")
        .select("*, scholarships (id, name)")
        .eq("scholarship_id", scholarshipId)
        .order("season", { ascending: false })
        .order("award_date", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      debugLog.error("scholarshipAwardsService.getByScholarship", error);
      throw error;
    }
  },

  async getBySeason(season) {
    try {
      const { data, error } = await supabase
        .from("scholarship_awards")
        .select("*, scholarships (id, name)")
        .eq("season", season)
        .order("scholarship_id", { ascending: true })
        .order("student_name", { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      debugLog.error("scholarshipAwardsService.getBySeason", error);
      throw error;
    }
  },

  async create(awardData) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("scholarship_awards")
        .insert({
          scholarship_id: awardData.scholarshipId,
          student_name: awardData.studentName,
          student_email: awardData.studentEmail || null,
          award_amount: awardData.awardAmount,
          season: awardData.season,
          award_date: awardData.awardDate || null,
          notes: awardData.notes || null,
          created_by: user?.id || null,
        })
        .select()
        .single();

      if (error) throw error;
      debugLog.action("Scholarship award created", {
        scholarshipId: awardData.scholarshipId,
        student: awardData.studentName,
      });
      return data;
    } catch (error) {
      debugLog.error("scholarshipAwardsService.create", error);
      throw error;
    }
  },

  async update(id, updates) {
    try {
      const payload = {};
      if (updates.studentName !== undefined) payload.student_name = updates.studentName;
      if (updates.studentEmail !== undefined) payload.student_email = updates.studentEmail;
      if (updates.awardAmount !== undefined) payload.award_amount = updates.awardAmount;
      if (updates.season !== undefined) payload.season = updates.season;
      if (updates.awardDate !== undefined) payload.award_date = updates.awardDate;
      if (updates.notes !== undefined) payload.notes = updates.notes;

      const { data, error } = await supabase
        .from("scholarship_awards")
        .update(payload)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      debugLog.error("scholarshipAwardsService.update", error);
      throw error;
    }
  },

  async remove(id) {
    try {
      const { error } = await supabase
        .from("scholarship_awards")
        .delete()
        .eq("id", id);

      if (error) throw error;
      debugLog.action("Scholarship award deleted", { id });
    } catch (error) {
      debugLog.error("scholarshipAwardsService.remove", error);
      throw error;
    }
  },
};
